<script lang="ts">
  import CameraView from "../camera/CameraView.svelte";
  import CornerEditor from "../edge/CornerEditor.svelte";
  import FileInputFallback from "../camera/FileInputFallback.svelte";
  import type { Corner } from "../edge/autoDetect";
  import type { CaptureSession } from "../state/captureSession";
  import { blobToImage, canvasToJpeg } from "../utils/canvas";
  import { Button } from "$lib/components/ui/button";
  import * as Alert from "$lib/components/ui/alert";

  interface Props {
    session: CaptureSession;
    onClose: () => void;
  }
  const { session, onClose }: Props = $props();

  type Stage =
    | { kind: "camera" }
    | { kind: "editing"; raw: Blob; corners: Corner[] }
    | { kind: "fallback"; reason: string };

  let stage = $state<Stage>({ kind: "camera" });
  let pages = $state<Blob[]>([]);
  let pendingFiles = $state<File[]>([]);
  let working = $state(false);

  async function onCapture(raw: Blob) {
    stage = { kind: "editing", raw, corners: [] };
  }

  async function confirmCorners(corners: Corner[]) {
    if (stage.kind !== "editing") return;
    const raw = stage.raw;
    working = true;
    try {
      const cropped = await cropToBlob(raw, corners);
      pages = [...pages, cropped];
      const [next, ...rest] = pendingFiles;
      pendingFiles = rest;
      if (next) {
        stage = { kind: "camera" };
        window.setTimeout(() => void onCapture(next), 0);
      } else {
        stage = { kind: "camera" };
      }
    } catch {
      pages = [...pages, raw];
      stage = { kind: "camera" };
    } finally {
      working = false;
    }
  }

  function useOriginal() {
    if (stage.kind !== "editing") return;
    pages = [...pages, stage.raw];
    const [next, ...rest] = pendingFiles;
    pendingFiles = rest;
    stage = { kind: "camera" };
    if (next) window.setTimeout(() => void onCapture(next), 0);
  }

  function retake() {
    stage = { kind: "camera" };
  }

  async function done() {
    if (pages.length === 0) {
      onClose();
      return;
    }
    await session.ingestImages(pages);
    onClose();
  }

  function onError(_msg: string) {
    stage = { kind: "fallback", reason: "Camera unavailable. Pick from library instead." };
  }

  async function onFallbackFiles(files: File[]) {
    const [first, ...rest] = files;
    if (!first) return;
    pendingFiles = rest;
    await onCapture(first);
  }

  async function cropToBlob(source: Blob, corners: Corner[]): Promise<Blob> {
    if (corners.length !== 4) return source;

    const img = await blobToImage(source);
    const left = Math.max(0, Math.floor(Math.min(...corners.map((p) => p.x))));
    const top = Math.max(0, Math.floor(Math.min(...corners.map((p) => p.y))));
    const right = Math.min(img.naturalWidth, Math.ceil(Math.max(...corners.map((p) => p.x))));
    const bottom = Math.min(img.naturalHeight, Math.ceil(Math.max(...corners.map((p) => p.y))));
    const width = right - left;
    const height = bottom - top;
    if (width <= 0 || height <= 0) return source;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return source;

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.save();
    ctx.beginPath();
    corners.forEach((point, index) => {
      const x = point.x - left;
      const y = point.y - top;
      if (index === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, left, top, width, height, 0, 0, width, height);
    ctx.restore();
    return canvasToJpeg(canvas, 0.9);
  }
</script>

{#if stage.kind === "camera"}
  <div class="fixed inset-0">
    <CameraView {onCapture} {onError} onCancel={onClose} />
    {#if pages.length > 0}
      <Button class="absolute bottom-24 right-4 h-12 rounded-full bg-primary px-5 font-black text-primary-foreground shadow-[0_14px_38px_oklch(0.84_0.144_83/0.3)]" onclick={done} disabled={working}>
        Done ({pages.length})
      </Button>
    {/if}
  </div>
{:else if stage.kind === "editing"}
  <CornerEditor
    imageBlob={stage.raw}
    initialCorners={stage.corners.length === 4 ? stage.corners : undefined}
    onConfirm={confirmCorners}
    onUseOriginal={useOriginal}
    onCancel={retake}
  />
{:else}
  <div class="scanlet-shell fixed inset-0 flex flex-col items-center justify-center gap-4 bg-background p-8">
    <Alert.Root class="glass-panel max-w-sm rounded-2xl border-border/70">
      <Alert.Description>{stage.reason}</Alert.Description>
    </Alert.Root>
    <FileInputFallback label="Choose photo" multiple={true} onSelect={onFallbackFiles} />
    <Button variant="outline" class="rounded-full border-border/70 bg-white/5 text-foreground" onclick={onClose}>Cancel</Button>
  </div>
{/if}
